import React, { useEffect, useMemo } from 'react';
import AppSelect from './AppSelect';
import useRemoteEntityOptions from '../../hooks/useRemoteEntityOptions';

/**
 * Select با جستجو و صفحه‌بندی سمت سرور برای محصولات / تگ‌ها / شیوه‌های آشنایی (و ...).
 * داخل Form.Item هم کار می‌کند (value / onChange).
 *
 * props:
 *  - kind: 'products' | 'tags' | 'sources' | 'leads' | 'clients'
 *  - knownOptions: گزینه‌هایی که از قبل معلوم‌اند ({ value, label, color? }) — بدون درخواست اضافه
 *  - leadingOptions: گزینه‌های ثابت بالای لیست، مثل [{ value: 'all', label: 'همه' }]
 *  - showColor: نمایش نقطه رنگی (برای تگ‌ها)
 */
const RemoteSelect = React.forwardRef(
  ({ kind, value, onChange, knownOptions, leadingOptions = [], showColor = false, style, ...rest }, ref) => {
    const selected = useMemo(
      () => [].concat(value ?? []).filter((v) => v !== 'all' && v !== '' && v != null),
      [value],
    );
    const remote = useRemoteEntityOptions(kind, { selectedValue: selected });
    const { addOptions } = remote;

    // کلید پایدار تا آرایه‌ی inline در هر رندر setState تکراری نزند
    const knownKey = JSON.stringify((knownOptions || []).map((o) => [Number(o.value), o.label, o.color || '']));
    useEffect(() => {
      const list = JSON.parse(knownKey).map(([v, label, color]) => ({ value: v, label, color: color || undefined }));
      if (list.length) addOptions(list);
    }, [knownKey, addOptions]);

    const toNum = (v) => (v === 'all' ? v : Number(v));
    const normalizedValue = Array.isArray(value)
      ? value.map(toNum)
      : value === '' || value == null
        ? undefined
        : toNum(value);

    const options = [
      ...leadingOptions,
      ...remote.options.map((o) => ({
        value: o.value,
        label: o.mobile ? `${o.label} (${o.mobile})` : o.label,
        color: o.color,
      })),
    ];

    return (
      <AppSelect
        ref={ref}
        value={normalizedValue}
        onChange={onChange}
        allowClear
        style={{ width: '100%', fontFamily: 'CustomFont', ...style }}
        {...remote.selectProps}
        options={options}
        optionRender={
          showColor
            ? (opt) => (
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: opt.data.color || '#1890ff' }}
                />
                {opt.data.label}
              </span>
            )
            : undefined
        }
        {...rest}
      />
    );
  },
);

RemoteSelect.displayName = 'RemoteSelect';

export default RemoteSelect;
